import React, { useRef } from "react";
import moment from "moment";
import { Stack, Typography, styled } from "@mui/material";
import BadgeVisual from "../visuals/BadgeVisual";
import EventDetailCard from "./EventDetailCard";

const Card = styled(Stack)(({ theme }: any) => ({
  backgroundColor: "#fff",
  border: `2px solid ${theme.palette.common.backgroundGrey}`,
  "&:hover": {
    "& .detail": {
      display: "flex"
    }
  },
  "& .detail": {
    position: "absolute",
    display: "none"
  }
}));

const StyledImageBox = styled("div", {
  shouldForwardProp: (prop: any) => prop !== "tag" && prop !== "src"
})<{ src: string; tag?: string; theme?: any }>(({ src }) => ({
  height: 120,
  width: "100%",
  background: `url(${src})`,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  borderRadius: "10px",
  position: "relative",
  "& .tag": {
    position: "absolute",
    right: 0
  }
}));

interface ImageBoxInterface {
  tag?: string;
  src: string;
}

const ImageBox = ({ tag, src }: ImageBoxInterface) => {
  return (
    <StyledImageBox src={src} tag={tag}>
      {tag
        ? <div className="tag">
            <BadgeVisual
              label={tag}
              color={tag === "conference" ? "green" : "blue"}
              borderPlacement="bottom-left"
            />
          </div>
        : null}
    </StyledImageBox>
  );
};

export interface EventCardGridViewInterface {
  eventName: string;
  eventDay: string;
  companyHost: string;
  date?: string;
  time?: string;
  location?: string;
  image?: string;
  tag?: string;
  daysLeft?: number;
  onShare: () => void;
  categories?: string[];
  onDetail: () => void;
  onInterest: () => void;
  height?: number;
}

const EventCardGridView = ({
  eventName,
  eventDay,
  date,
  time,
  location,
  image,
  tag,
  companyHost,
  daysLeft,
  onShare,
  onDetail,
  onInterest,
  categories,
  height
}: EventCardGridViewInterface) => {
  const myRef: any = useRef();

  return (
    <Card
      boxShadow="5px -3px 20px rgb(0 0 0 / 25%)"
      borderRadius={2.5}
      position="relative"
      width="100%"
      height={height || 380}
    >
      <Stack py={3} px={2} height="100%">
        <Typography
          fontSize={16}
          fontWeight={700}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "inherit",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical"
          }}
        >
          {eventName}
        </Typography>
        <Stack flex={1} justifyContent="flex-end" spacing={2.7}>
          <Typography className="font-grey" variant="h5" fontWeight={400}>
            {companyHost}
          </Typography>

          <ImageBox
            src={image ? image : "/assets/images/default-img.jpg"}
            tag={tag}
          />

          <Stack direction="row">
            <Stack
              className="border-1-primary"
              borderRadius={2}
              width={95}
              minWidth={95}
              height={88}
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="body2"
                fontWeight={400}
                className="font-grey"
              >
                {eventDay}
              </Typography>
              <Typography variant="h4" color="primary" fontWeight={700}>
                {`${moment(date).format("MMM")}  ${moment(date).format("DD")}`}
              </Typography>
              <Typography variant="h5" className="font-grey" fontWeight={400}>
                {moment(date).format("YYYY")}
              </Typography>
            </Stack>
            <Stack
              spacing={1}
              pl={3}
              pt={1}
              width="calc(100% - 120px)"
              ref={myRef}
            >
              {date &&
                <Typography variant="h6" className="font-grey" fontWeight={400}>
                  {date}
                </Typography>}
              {time &&
                <Typography variant="h6" className="font-grey" fontWeight={400}>
                  {time}
                </Typography>}
              {location &&
                <Typography
                  variant="h6"
                  className="font-grey"
                  fontWeight={400}
                  style={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    maxWidth:
                      myRef && myRef.current
                        ? myRef.current.offsetWidth
                        : "100%"
                  }}
                >
                  {location}
                </Typography>}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack className="detail background-white" width="100%" height="100%">
        <EventDetailCard
          days={daysLeft}
          eventName={eventName}
          onShare={onShare}
          categories={categories}
          onDetail={onDetail}
          onInterest={onInterest}
        />
      </Stack>
    </Card>
  );
};
export default EventCardGridView;
